import { apiClient } from '../client';
import type { CsTicketStatus, TicketListFilters } from './tickets.api';

/**
 * Client for the CS agent-workspace helpers (Phase 16 / ADR-026):
 *
 *   - `/api/cs/macros` — canned action bundles (read: use_cs; write:
 *     manage_cs_routing). server/src/routes/cs/macros.routes.ts
 *   - `/api/cs/views`  — per-user saved queue views (use_cs).
 *     server/src/routes/cs/saved-views.routes.ts
 */

// ── Macros ──────────────────────────────────────────────────────────────

export interface CsMacro {
  id: string;
  organizationId: string;
  shortCode: string;
  title: string;
  replyText: string | null;
  addTags: string[];
  setStatus: CsTicketStatus | null;
  assignToMembershipId: string | null;
  internalNote: string | null;
  sopUrl: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function listCsMacros(): Promise<CsMacro[]> {
  const res = await apiClient.get<{ data: CsMacro[] }>('/api/cs/macros');
  return res.data.data;
}

export interface SaveMacroInput {
  shortCode: string;
  title: string;
  replyText?: string | null;
  addTags?: string[];
  setStatus?: CsTicketStatus | null;
  assignToMembershipId?: string | null;
  internalNote?: string | null;
  sopUrl?: string | null;
  isActive?: boolean;
}

export async function createCsMacro(input: SaveMacroInput): Promise<CsMacro> {
  const res = await apiClient.post<{ data: CsMacro }>('/api/cs/macros', input);
  return res.data.data;
}

export async function updateCsMacro(id: string, input: Partial<SaveMacroInput>): Promise<CsMacro> {
  const res = await apiClient.put<{ data: CsMacro }>(`/api/cs/macros/${encodeURIComponent(id)}`, input);
  return res.data.data;
}

export async function deleteCsMacro(id: string): Promise<void> {
  await apiClient.delete(`/api/cs/macros/${encodeURIComponent(id)}`);
}

// ── Saved views ─────────────────────────────────────────────────────────

export interface CsSavedView {
  id: string;
  organizationId: string;
  name: string;
  /** Null = shared team view (read-only from this surface). */
  ownerMembershipId: string | null;
  filtersJson: TicketListFilters;
  position: number;
  createdAt: string;
}

export async function listSavedViews(): Promise<CsSavedView[]> {
  const res = await apiClient.get<{ data: CsSavedView[] }>('/api/cs/views');
  return res.data.data;
}

export async function createSavedView(name: string, filters: TicketListFilters): Promise<CsSavedView> {
  const res = await apiClient.post<{ data: CsSavedView }>('/api/cs/views', { name, filters });
  return res.data.data;
}

export async function updateSavedView(
  id: string,
  input: { name?: string; filters?: TicketListFilters; position?: number },
): Promise<CsSavedView> {
  const res = await apiClient.put<{ data: CsSavedView }>(`/api/cs/views/${encodeURIComponent(id)}`, input);
  return res.data.data;
}

export async function deleteSavedView(id: string): Promise<void> {
  await apiClient.delete(`/api/cs/views/${encodeURIComponent(id)}`);
}
